import { type WorkflowSupportedIntegration, isWorkflowSupportedIntegration } from '../../../../../../common/integrations'
import { looksLikeHtmlFragment } from '../../../../../../components/CustomWorkflows/htmlDocument'
import {
  type IntegrationWorkflow,
  type IntegrationWorkflowCapabilities,
  IntegrationWorkflowStatus,
  type IntegrationWorkflowStep,
  type IntegrationWorkflowTemplate,
} from '../../../../../../models/IntegrationWorkflow'
import { type AdditionalObjects } from '../../../../../../models/Integration'
import { type SuggestedWorkflows } from '../../../../../../models/SuggestedWorkflow'
import { type User } from '../../../../../../models/User'
import { type RawWorkflowLists } from '../../../../../../store/integrations/workflowLists'
import { escapeHtml } from '../../../common/aifeatures/utils'

export interface IntegrationWorkflowTarget {
  serviceId: WorkflowSupportedIntegration
  externalId: string
  contactId: string
}

export type IntegrationWorkflowLists = {
  running: IntegrationWorkflow[]
  completed: IntegrationWorkflow[]
  templates: IntegrationWorkflowTemplate[]
}

export type StartIntegrationWorkflowInput = {
  templateId: string
  payload: Record<string, unknown>
}

export type CompleteIntegrationWorkflowStepInput = {
  outcomeId?: number | null
  note?: string
}

export interface IntegrationWorkflowAdapter<StartFields> {
  capabilities: IntegrationWorkflowCapabilities
  toLists: (additionalObjects: AdditionalObjects) => IntegrationWorkflowLists
  buildStartPayload: (fields: StartFields, externalId: string) => Record<string, unknown>
  buildCompleteStepPayload: (input: CompleteIntegrationWorkflowStepInput) => Record<string, unknown>
}

export type HandledSuggestions = {
  workflowIds: number[]
  templateIds: string[]
}

export type IntegrationWorkflowSuggestions = {
  workflows: IntegrationWorkflow[]
  templates: { template: IntegrationWorkflowTemplate; label: string | null; summary: string | null }[]
}

type RawWorkflow<S> = {
  id: number
  name?: string | null
  description?: string | null
  label?: string | null
  completedAt?: string | null
  activeStep?: S | null
  workflowSteps?: S[]
}

export const toIntegrationWorkflow = <S>(workflow: RawWorkflow<S>, toStep: (step: S) => IntegrationWorkflowStep): IntegrationWorkflow => {
  const completedAt = workflow.completedAt ?? null
  const activeStep = !completedAt && workflow.activeStep ? toStep(workflow.activeStep) : null
  const steps = workflow.workflowSteps?.length ? workflow.workflowSteps.map(toStep) : activeStep ? [activeStep] : []
  return {
    id: workflow.id,
    name: workflow.name ?? null,
    description: workflow.description ?? null,
    label: workflow.label ?? null,
    status: completedAt ? IntegrationWorkflowStatus.COMPLETED : IntegrationWorkflowStatus.RUNNING,
    completedAt,
    activeStep,
    steps,
  }
}

const byNewestCompletion = (a: IntegrationWorkflow, b: IntegrationWorkflow) => (b.completedAt ?? '').localeCompare(a.completedAt ?? '')

export const toIntegrationWorkflowLists = <W, T>(
  lists: RawWorkflowLists<W> & { workflowTemplates?: T[] },
  toWorkflow: (workflow: W) => IntegrationWorkflow,
  toTemplate: (template: T) => IntegrationWorkflowTemplate,
): IntegrationWorkflowLists => ({
  running: (lists.workflows ?? []).map(toWorkflow),
  completed: (lists.completedWorkflows ?? []).map(toWorkflow).sort(byNewestCompletion),
  templates: (lists.workflowTemplates ?? []).map(toTemplate),
})

export const toIntegrationWorkflowTarget = (serviceId: string, contact: User): IntegrationWorkflowTarget | null => {
  const externalId = contact.integrations?.find((integration) => integration.serviceId === serviceId)?.externalId
  return isWorkflowSupportedIntegration(serviceId) && externalId ? { serviceId, externalId, contactId: contact.id } : null
}

export const getVisibleWorkflowSteps = ({ steps, activeStep }: IntegrationWorkflow): IntegrationWorkflowStep[] => {
  const visibleSteps = steps.filter((step) => step.completedAt || step.id === activeStep?.id)
  return activeStep && !visibleSteps.some((step) => step.id === activeStep.id) ? [...visibleSteps, activeStep] : visibleSteps
}

export const DEFAULT_WORKFLOW_DESCRIPTION = 'Started from Zocks after a client meeting.'

const toHtmlParagraphs = (text: string) =>
  text
    .split(/\n+/)
    .filter((line) => line.trim())
    .map((line) => `<p>${escapeHtml(line.trim())}</p>`)
    .join('')

export const buildWorkflowDescriptionHtml = (templateDescription: string | null, summary?: string | null) => {
  const description = templateDescription?.trim() || DEFAULT_WORKFLOW_DESCRIPTION
  const descriptionHtml = looksLikeHtmlFragment(description) ? description : toHtmlParagraphs(description)
  return summary ? descriptionHtml + toHtmlParagraphs(summary) : descriptionHtml
}

export const getWorkflowStepPosition = ({ steps, activeStep }: IntegrationWorkflow): { number: number; total: number } | null => {
  const index = activeStep ? steps.findIndex((step) => step.id === activeStep.id) : -1
  return index === -1 ? null : { number: index + 1, total: steps.length }
}

export const workflowStepEntityId = (workflowId: number, stepId: number) => `${workflowId}-${stepId}`

export const selectSuggestions = (
  result: SuggestedWorkflows | null,
  lists: IntegrationWorkflowLists,
  handled: HandledSuggestions,
): IntegrationWorkflowSuggestions => {
  if (!result) {
    return { workflows: [], templates: [] }
  }
  const suggestedWorkflowIds = result.workflows.map((workflow) => `${workflow.id}`)
  const suggestedTemplates = new Map(result.workflowTemplates.map((template) => [`${template.id}`, template]))
  return {
    workflows: lists.running.filter((workflow) => suggestedWorkflowIds.includes(`${workflow.id}`) && !handled.workflowIds.includes(workflow.id)),
    templates: lists.templates
      .filter((template) => suggestedTemplates.has(template.id) && !handled.templateIds.includes(template.id))
      .map((template) => ({
        template,
        label: suggestedTemplates.get(template.id)?.label ?? null,
        summary: suggestedTemplates.get(template.id)?.summary?.trim() || null,
      })),
  }
}
